import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import dsaImage from "../../assets/dsa.webp";
import lenderImage from "../../assets/lenders.webp";

const roles = [
  {
    title: "For DSAs",
    description:
      "Submit from your phone. Track status. Get paid against the disbursal, not a promise.",
    button: "Join as a DSA",
    href: "/for-dsas",
    image: dsaImage,
    theme: "dark",
  },
  {
    title: "For Lenders",
    description:
      "Pick the products and ticket sizes you want. Receive pre-verified leads only.",
    button: "Partner as a lender",
    href: "/for-lenders",
    image: lenderImage,
    theme: "light",
  },
];

function RoleSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.15,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="overflow-hidden bg-[#191e1c]">
      {/* =====================================================
          ROLE CARDS (Smooth Luxury Dual Slide-In on Scroll)
      ====================================================== */}
      <div className="grid grid-cols-1 overflow-hidden lg:grid-cols-2">
        {roles.map((role, idx) => {
          const isDark = role.theme === "dark";
          const isLeft = idx === 0;

          return (
            <article
              key={role.title}
              className={`group flex min-h-[560px] flex-col justify-center px-6 py-14 sm:min-h-[600px] sm:px-10 sm:py-16 lg:min-h-[620px] lg:px-12 lg:py-20 xl:px-[6.7vw] transition-all duration-[1200ms] [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] ${
                isVisible
                  ? "opacity-100 translate-x-0"
                  : isLeft
                  ? "opacity-0 -translate-x-8 sm:-translate-x-14"
                  : "opacity-0 translate-x-8 sm:translate-x-14"
              } ${
                isDark
                  ? "bg-[#191e1c] text-white"
                  : "bg-[#ffffff] text-[#10191c]"
              }`}
            >
              <div className="mx-auto w-full max-w-[550px]">
                {/* Image */}
                <div className="flex w-full justify-center">
                  <div className="w-full max-w-[320px] overflow-hidden rounded-[17px] sm:max-w-[340px]">
                    <img
                      src={role.image}
                      alt={role.title}
                      className="block h-auto w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105 group-hover:-translate-y-1.5"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="mt-6">
                  <p
                    className={`font-jakarta text-[11px] font-medium tracking-[0.34em] uppercase mb-2 ${
                      isDark ? "text-[var(--mint-green)]" : "text-[var(--hero-green)]"
                    }`}
                  >
                    {isDark ? "DSA DISTRIBUTION" : "LENDER NETWORK"}
                  </p>

                  <h2
                    className={`font-bricolage text-[22px] font-semibold leading-[1.1] tracking-[-0.6px] sm:text-[24px] ${
                      isDark ? "text-white" : "text-[#10191c]"
                    }`}
                  >
                    {role.title}
                  </h2>

                  <p
                    className={`mt-3 font-jakarta text-[12px] leading-[1.6] sm:text-[13px] ${
                      isDark ? "text-[#aeb5b1]" : "text-[#56635f]"
                    }`}
                  >
                    {role.description}
                  </p>

                  {/* Button */}
                  <Link
                    to={role.href}
                    className="mt-5 inline-flex min-h-[38px] items-center justify-center rounded-full bg-[var(--mint-green)] px-5 font-jakarta text-[12px] font-semibold text-[#071b17] transition-all duration-200 hover:-translate-y-0.5 hover:scale-[1.02] hover:opacity-95 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 active:outline-none select-none shadow-sm"
                  >
                    {role.button}
                  </Link>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default RoleSection;